import {
    AddCommentParams,
    Model,
    UpdateCommentParams,
    WidgetDisplayMode,
    WidgetWrapperProps,
    jUserList,
    JFilebankApi,
    JFileApi,
    JSocialActionApi,
    JUserApi,
    CommentWithExtensions,
} from 'jamespot-user-api';
import { jCommentList } from 'jamespot-user-api/lib/src/types/comment';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { JRCInputTinyMCERawProps } from '../Form/Input/JRCInputTinyMCERaw/JRCInputTinyMCERaw';
import { JRCComment } from './JRCComment';

import { DriveOrFilebank } from '../Form/Input/JRCInputFileAdvanced/JRCInputFileAdvancedRaw';
import { JDriveApi } from 'jamespot-user-api/lib/src/apis/drive/drive';
import { JQuestionAnswerApi } from 'jamespot-user-api/lib/src/apis/questionAnswer/questionAnswer';
import { CommentsHeader } from './components/CommentsHeader';
import { NewComment } from './components/NewComment';
import { TinyMCEExtension } from '../Form/Input/JRCInputTinyMCERaw/extensions/JTinyMCEExtensions';

export type JRCCommentsBlocProps = {
    comments?: Array<CommentWithExtensions<jCommentList, ['actions']> & { pending?: boolean }>;
    initialCommentsNumber?: number;
    currentUser: jUserList;
    tinyMCEConfig: Pick<JRCInputTinyMCERawProps, 'commonOptions' | 'mentionsQueries' | 'tinymceScriptSrc'>;
    onComment: (params: Pick<AddCommentParams, 'content' | 'alertAuthor' | 'sendAlert'>) => Promise<void>;
    isWidgetActive?: boolean;
    canCreateWidget?: boolean;
    widgetListComponent: React.ReactNode;
    newWidgetsWrapperComponent: React.ReactNode;
    existingWidgetsWrapperComponent: (
        comment: jCommentList,
        widgets: WidgetWrapperProps[],
        mode: WidgetDisplayMode,
        inplace: boolean,
    ) => React.ReactNode;
    loading?: boolean;
    token?: string;
    onCommentUpdate: (args: UpdateCommentParams) => Promise<void>;
    onCommentDelete: (idComment: number) => Promise<void>;
    onError?: (message?: string) => void;
    highlightFields?: string[];
    userModel?: Model;
    userAccountStatus?: 0 | 1;
    isWedocActive: boolean;
    getAccessHash?: (fileId: number) => Promise<string>;
    onGetHashError: (message?: string) => void;
    activeDrives: DriveOrFilebank[];
    variant?: 'modal' | 'inline';
    canComment: boolean;
    handlers: {
        filebank: Pick<JFilebankApi, 'getBanks' | 'getFolders' | 'getDocuments'>;
        file: Pick<JFileApi, 'copy' | 'deleteFile'>;
        drive: Pick<
            JDriveApi,
            | 'msDocuments'
            | 'dropboxDocuments'
            | 'groups'
            | 'tenants'
            | 'listAccount'
            | 'isAccountLogged'
            | 'checkAccountToken'
            | 'getAccount'
            | 'logout'
            | 'setDefaultAccount'
            | 'getDocument'
        >;
        user: Pick<JUserApi, 'get' | 'getFields'>;
        socialAction: Pick<JSocialActionApi, 'add' | 'remove' | 'getUsers'>;
        questionAnswer: Pick<JQuestionAnswerApi, 'selectResponse' | 'unselectResponse'>;
    };
    socialQuestion?: {
        isResolved: boolean;
        responseId?: number;
        onSelectResponse: (responseId: number) => void;
        onUnselectResponse: () => void;
        canSelectResponse: boolean;
    };
    articleId: number;
    additionalExtensions: Array<TinyMCEExtension>;
    onReport: (commentUri: string) => void;
};

const Container = styled.div<{ marginY: number }>`
    background-color: ${({ theme }) => theme.color.grey0};
    border-radius: 8px;
    margin: 0 ${({ marginY }) => marginY}px;
`;

const CommentsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space.sm}px;
`;

export const AvatarWrapper = styled.div`
    & *:hover {
        cursor: pointer;
    }
`;

export const JRCCommentsBloc = ({
    comments,
    initialCommentsNumber = 5,
    currentUser,
    tinyMCEConfig,
    onComment,
    canCreateWidget = false,
    isWidgetActive = false,
    widgetListComponent,
    newWidgetsWrapperComponent,
    existingWidgetsWrapperComponent,
    loading,
    token,
    onCommentDelete,
    onCommentUpdate,
    onError,
    highlightFields,
    userModel,
    userAccountStatus,
    isWedocActive,
    getAccessHash,
    onGetHashError,
    activeDrives,
    variant,
    canComment,
    handlers,
    socialQuestion,
    articleId,
    additionalExtensions,
    onReport,
}: JRCCommentsBlocProps) => {
    const commentsContainerRef = useRef<HTMLDivElement>(null);
    const [focusedCommentIndex, setFocusedCommentIndex] = useState<number>();
    const [displayedCommentsNumber, setDisplayedCommentsNumber] = useState(initialCommentsNumber);

    const displayMoreComments = useCallback(() => {
        setDisplayedCommentsNumber(displayedCommentsNumber + 5);
    }, [displayedCommentsNumber]);

    const focusLastComment = useCallback(() => {
        if (comments) {
            setFocusedCommentIndex(comments.length - 1);
            if (commentsContainerRef?.current) {
                commentsContainerRef.current.scrollIntoView({ block: 'end' });
            }
        }
    }, [comments]);

    const deleteFile = useCallback(
        async (id: number) => {
            if (!token) return;
            try {
                await handlers.file.deleteFile(id, 'attachment', token);
            } catch (e: any) {
                if (onError) onError(e);
            }
        },
        [handlers.file, onError, token],
    );

    useEffect(() => {
        const eventHandler = () => {
            setFocusedCommentIndex(undefined);
        };

        if (focusedCommentIndex !== undefined) {
            document.addEventListener('click', eventHandler);
        } else {
            document.removeEventListener('click', eventHandler);
        }

        return () => {
            document.removeEventListener('click', eventHandler);
        };
    }, [focusedCommentIndex]);

    return (
        <>
            <Container marginY={variant === 'modal' ? 8 : 0}>
                <CommentsHeader
                    displayedCommentsNumber={displayedCommentsNumber}
                    displayMoreComments={displayMoreComments}
                    focusLastComment={focusLastComment}
                    commentsCount={comments?.length}
                    lastCommentUser={comments?.[comments.length - 1]?._user}
                    variant={variant}
                />
                {comments && (
                    <CommentsContainer ref={commentsContainerRef}>
                        {comments.map((c, i) =>
                            comments.length - i <= displayedCommentsNumber ? (
                                <div key={`comment-${i}`}>
                                    <JRCComment
                                        isFocused={i === focusedCommentIndex}
                                        comment={c}
                                        tinyMCEConfig={tinyMCEConfig}
                                        onCommentDelete={onCommentDelete}
                                        onCommentUpdate={onCommentUpdate}
                                        deleteFile={deleteFile}
                                        token={token}
                                        onError={onError}
                                        highlightFields={highlightFields}
                                        userModel={userModel}
                                        userAccountStatus={userAccountStatus}
                                        isWidgetActive={isWidgetActive}
                                        onGetHashError={onGetHashError}
                                        isWedocActive={isWedocActive}
                                        getAccessHash={getAccessHash}
                                        existingWidgetsWrapperComponent={existingWidgetsWrapperComponent}
                                        handlers={handlers}
                                        activeDrives={activeDrives}
                                        socialQuestion={socialQuestion}
                                        articleId={articleId}
                                        onReport={onReport}
                                    />
                                </div>
                            ) : null,
                        )}
                    </CommentsContainer>
                )}
                <NewComment
                    currentUser={currentUser}
                    widgetListComponent={widgetListComponent}
                    newWidgetsWrapperComponent={newWidgetsWrapperComponent}
                    canComment={canComment}
                    onComment={onComment}
                    tinyMCEConfig={tinyMCEConfig}
                    activeDrives={activeDrives}
                    socialQuestion={!!socialQuestion}
                    token={token}
                    onError={onError}
                    handlers={handlers}
                    canCreateWidget={canCreateWidget}
                    deleteFile={deleteFile}
                    loading={loading}
                    additionalExtensions={additionalExtensions}
                />
            </Container>
        </>
    );
};
